// src/pages/Loans.js  (Admin panel)
// Add to admin index.js:
//   import Loans from './pages/Loans';
//   <Route path="/loans" element={<PrivateRoute title="QueeKLoan"><Loans /></PrivateRoute>} />
// Add to Sidebar navItems:
//   { path: '/loans', icon: '💰', label: 'QueeKLoan' }
// Add to adminAPI (api.js):
//   loanPlans: () => api.get('/admin/loan-plans'),
//   createLoanPlan: (data) => api.post('/admin/loan-plans', data),
//   updateLoanPlan: (id, data) => api.patch(`/admin/loan-plans/${id}`, data),
//   deleteLoanPlan: (id) => api.delete(`/admin/loan-plans/${id}`),
//   loans: (params) => api.get('/admin/loans', { params }),
//   processLoan: (id, data) => api.post(`/admin/loans/${id}/process`, data),

import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { adminAPI } from '../api';

const PLAN_DEFAULTS = {
  name: '', description: '', image_url: '', amount: '',
  interest_rate: '', duration_days: 30, is_active: 1, sort_order: 0,
};

function StatusBadge({ status }) {
  const map = {
    pending:   { label: 'Pending',   cls: 'badge-pending' },
    active:    { label: 'Active',    cls: 'badge-active' },
    repaid:    { label: 'Repaid',    cls: 'badge-repaid' },
    rejected:  { label: 'Rejected',  cls: 'badge-suspended' },
    defaulted: { label: 'Defaulted', cls: 'badge-suspended' },
  };
  const s = map[status] || { label: status, cls: '' };
  return <span className={`badge ${s.cls}`}>{s.label}</span>;
}

export default function Loans() {
  const [tab,          setTab]          = useState('plans');   // 'plans' | 'applications'
  const [plans,        setPlans]        = useState([]);
  const [applications, setApplications] = useState([]);
  const [filterStatus, setFilterStatus] = useState('');
  const [showForm,     setShowForm]     = useState(false);
  const [editing,      setEditing]      = useState(null);
  const [form,         setForm]         = useState(PLAN_DEFAULTS);
  const [processing,   setProcessing]   = useState(null); // { loan, action }
  const [adminNote,    setAdminNote]    = useState('');

  const loadPlans = () =>
    adminAPI.loanPlans().then(r => setPlans(r.data.loan_plans || [])).catch(() => {});

  const loadApps = (status = filterStatus) =>
    adminAPI.loans(status ? { status } : {}).then(r => setApplications(r.data.loans || [])).catch(() => {});

  useEffect(() => { loadPlans(); }, []);
  useEffect(() => { if (tab === 'applications') loadApps(); }, [tab, filterStatus]); // eslint-disable-line

  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const openAdd  = () => { setEditing(null); setForm(PLAN_DEFAULTS); setShowForm(true); };
  const openEdit = (p) => { setEditing(p); setForm(p); setShowForm(true); };

  const savePlan = async () => {
    try {
      if (editing) {
        await adminAPI.updateLoanPlan(editing.id, form);
        toast.success('Loan plan updated');
      } else {
        await adminAPI.createLoanPlan(form);
        toast.success('Loan plan created');
      }
      setShowForm(false);
      loadPlans();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save');
    }
  };

  const deletePlan = async (id) => {
    if (!window.confirm('Delete this loan plan?')) return;
    try { await adminAPI.deleteLoanPlan(id); toast.success('Deleted'); loadPlans(); }
    catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  const openProcess = (loan, action) => { setProcessing({ loan, action }); setAdminNote(''); };

  const confirmProcess = async () => {
    if (!processing) return;
    try {
      await adminAPI.processLoan(processing.loan.id, { action: processing.action, admin_note: adminNote });
      toast.success(`Loan ${processing.action}d`);
      setProcessing(null);
      loadApps();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed');
    }
  };

  return (
    <div>
      {/* ── Plan Form Modal ── */}
      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal-box" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
            <div className="modal-title">{editing ? 'Edit Loan Plan' : 'Add Loan Plan'}</div>
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Name *</label>
                <input className="form-control" value={form.name} onChange={e => setF('name', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Loan Amount (GHS) *</label>
                <input className="form-control" type="number" step="0.01" value={form.amount} onChange={e => setF('amount', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Interest Rate (%) *</label>
                <input className="form-control" type="number" step="0.01" value={form.interest_rate} onChange={e => setF('interest_rate', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Duration (Days)</label>
                <input className="form-control" type="number" value={form.duration_days} onChange={e => setF('duration_days', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Sort Order</label>
                <input className="form-control" type="number" value={form.sort_order} onChange={e => setF('sort_order', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Status</label>
                <select className="form-control" value={form.is_active} onChange={e => setF('is_active', e.target.value)}>
                  <option value={1}>Active</option>
                  <option value={0}>Inactive</option>
                </select>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Description</label>
              <textarea className="form-control" rows={3} value={form.description || ''} onChange={e => setF('description', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Image URL</label>
              <input
                className="form-control"
                value={form.image_url || ''}
                onChange={e => setF('image_url', e.target.value)}
                placeholder="https://..."
              />
              {form.image_url && (
                <img
                  src={form.image_url}
                  alt="preview"
                  onError={e => { e.target.style.display = 'none'; }}
                  style={{ marginTop: 8, height: 80, borderRadius: 8, objectFit: 'cover', border: '1px solid var(--gray-200)' }}
                />
              )}
            </div>
            {form.amount && form.interest_rate && (
              <div style={{
                background: 'var(--gray-50)', borderRadius: 8, padding: '10px 14px',
                marginBottom: 14, fontSize: 13, color: 'var(--gray-600)',
              }}>
                💡 Borrowers repay <strong>
                  GHS {(parseFloat(form.amount || 0) * (1 + parseFloat(form.interest_rate || 0) / 100)).toFixed(2)}
                </strong> (principal + {form.interest_rate}% interest)
              </div>
            )}
            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={() => setShowForm(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={savePlan}>Save Plan</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Process Loan Modal ── */}
      {processing && (
        <div className="modal-overlay" onClick={() => setProcessing(null)}>
          <div className="modal-box" onClick={e => e.stopPropagation()} style={{ maxWidth: 380 }}>
            <div className="modal-title">
              {processing.action === 'approve' ? '✅ Approve Loan' : '❌ Reject Loan'}
            </div>
            <div style={{ fontSize: 14, color: 'var(--gray-600)', marginBottom: 14 }}>
              <strong>{processing.loan.username}</strong> applied for{' '}
              <strong>GHS {parseFloat(processing.loan.amount).toFixed(2)}</strong> via{' '}
              <em>{processing.loan.plan_name}</em>.
              {processing.action === 'approve' && (
                <div style={{
                  marginTop: 10, background: '#d1e7dd', borderRadius: 8,
                  padding: '8px 12px', fontSize: 13, color: '#0a3622',
                }}>
                  This will <strong>credit GHS {parseFloat(processing.loan.amount).toFixed(2)}</strong> to the user's balance.
                </div>
              )}
            </div>
            <div className="form-group">
              <label className="form-label">Admin Note (optional)</label>
              <textarea
                className="form-control"
                rows={2}
                value={adminNote}
                onChange={e => setAdminNote(e.target.value)}
                placeholder="Reason for rejection or approval notes…"
              />
            </div>
            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={() => setProcessing(null)}>Cancel</button>
              <button
                className={`btn ${processing.action === 'approve' ? 'btn-primary' : 'btn-danger'}`}
                onClick={confirmProcess}
              >
                {processing.action === 'approve' ? 'Approve & Credit' : 'Reject Loan'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Tabs ── */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {[
          { id: 'plans',        label: '📋 Loan Plans' },
          { id: 'applications', label: '📥 Applications' },
        ].map(t => (
          <button
            key={t.id}
            className={`btn ${tab === t.id ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'plans' ? (
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
            <button className="btn btn-primary" onClick={openAdd}>+ Add Loan Plan</button>
          </div>
          <div className="table-card">
            <div className="table-header"><h3>Loan Plans</h3></div>
            <table>
              <thead>
                <tr>
                  <th>ID</th><th>Image</th><th>Name</th>
                  <th>Amount (GHS)</th><th>Interest %</th><th>Repayment</th>
                  <th>Duration</th><th>Status</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {plans.length === 0 ? (
                  <tr><td colSpan={9} style={{ textAlign: 'center', color: 'var(--gray-400)', padding: 24 }}>No loan plans yet.</td></tr>
                ) : plans.map(p => {
                  const repay = parseFloat(p.amount) * (1 + parseFloat(p.interest_rate) / 100);
                  return (
                    <tr key={p.id}>
                      <td>{p.id}</td>
                      <td>
                        {p.image_url
                          ? <img src={p.image_url} alt={p.name} onError={e => { e.target.style.display='none'; }}
                              style={{ width:48, height:48, borderRadius:8, objectFit:'cover', border:'1px solid var(--gray-200)' }} />
                          : <div style={{ width:48, height:48, borderRadius:8, background:'var(--gray-100)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:22 }}>💰</div>
                        }
                      </td>
                      <td style={{ fontWeight: 600 }}>{p.name}</td>
                      <td>GHS {parseFloat(p.amount).toFixed(2)}</td>
                      <td style={{ color: 'var(--gold)', fontWeight: 600 }}>{p.interest_rate}%</td>
                      <td>GHS {repay.toFixed(2)}</td>
                      <td>{p.duration_days} days</td>
                      <td><span className={`badge ${p.is_active ? 'badge-active' : 'badge-suspended'}`}>{p.is_active ? 'Active' : 'Inactive'}</span></td>
                      <td>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button className="btn btn-ghost btn-sm" onClick={() => openEdit(p)}>Edit</button>
                          <button className="btn btn-danger btn-sm" onClick={() => deletePlan(p.id)}>Delete</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <>
          {/* Filter bar */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center' }}>
            <span style={{ fontSize: 13, color: 'var(--gray-500)' }}>Filter:</span>
            {['', 'pending', 'active', 'repaid', 'rejected', 'defaulted'].map(s => (
              <button
                key={s || 'all'}
                className={`btn btn-sm ${filterStatus === s ? 'btn-primary' : 'btn-ghost'}`}
                onClick={() => setFilterStatus(s)}
              >
                {s || 'All'}
              </button>
            ))}
          </div>

          <div className="table-card">
            <div className="table-header"><h3>Loan Applications</h3></div>
            <table>
              <thead>
                <tr>
                  <th>ID</th><th>User</th><th>Plan</th>
                  <th>Amount</th><th>Repayment</th><th>Status</th>
                  <th>Due Date</th><th>Applied</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {applications.length === 0 ? (
                  <tr><td colSpan={9} style={{ textAlign: 'center', color: 'var(--gray-400)', padding: 24 }}>No applications found.</td></tr>
                ) : applications.map(loan => (
                  <tr key={loan.id}>
                    <td>{loan.id}</td>
                    <td>
                      <div style={{ fontWeight: 600 }}>{loan.username}</div>
                      <div style={{ fontSize: 11, color: 'var(--gray-400)' }}>{loan.email}</div>
                    </td>
                    <td>{loan.plan_name}</td>
                    <td>GHS {parseFloat(loan.amount).toFixed(2)}</td>
                    <td>GHS {parseFloat(loan.repayment_total).toFixed(2)}</td>
                    <td><StatusBadge status={loan.status} /></td>
                    <td>{loan.due_date ? new Date(loan.due_date).toLocaleDateString() : '—'}</td>
                    <td style={{ fontSize: 12, color: 'var(--gray-400)' }}>
                      {new Date(loan.created_at).toLocaleDateString()}
                    </td>
                    <td>
                      {loan.status === 'pending' ? (
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button className="btn btn-primary btn-sm" onClick={() => openProcess(loan, 'approve')}>
                            Approve
                          </button>
                          <button className="btn btn-danger btn-sm" onClick={() => openProcess(loan, 'reject')}>
                            Reject
                          </button>
                        </div>
                      ) : (
                        <span style={{ fontSize: 12, color: 'var(--gray-400)' }}>—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}